import { Link } from '../../../components/Link';
import { Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { useLegal } from './useLegal';
import { useLegalPagesVisible } from '../../../lib/useLegalPagesVisible';

/**
 * Privacy Policy — structured on the GDPR.eu template (https://gdpr.eu/privacy-notice/),
 * adapted for a SaaS that acts as both Controller (for the visitor data + account info)
 * and Processor (for the customer's submitted email lists).
 */
export function PrivacyPage() {
  const c = useLegal();
  const { isVisible } = useLegalPagesVisible();
  const subprocessorsPage = isVisible('subprocessors');
  return (
    <><Seo title="Privacy Policy" description={`How ${c.legalEntityName} collects, uses, and protects personal data — what we store, why we store it, how long we keep it, and the rights you can exercise.`} path="/privacy" /><LegalLayout title="Privacy Policy" lastUpdated={c.lastUpdatedAt}>
      <p>
        This Privacy Policy explains how <strong>{c.legalEntityName}</strong>
        {!c.hideBusinessIdentity && (
          <>
            {' '}({c.companyType}, VAT {c.vatNumber}, registered at{' '}
            {c.registeredAddressLine})
          </>
        )}{' '}(&quot;<strong>SieveGuard</strong>&quot;, &quot;<strong>we</strong>&quot;,
        &quot;<strong>us</strong>&quot;) collects, uses, and protects personal data when you
        visit <code>{c.websiteUrl}</code> or use the SieveGuard service.
      </p>

      <h2>1. Who is the data controller</h2>
      <p>
        For data we collect about <em>you as a visitor or customer</em> (account, billing,
        usage), {c.legalEntityName} is the Controller. For email data{' '}
        <em>you submit through the SieveGuard service to verify or analyse</em>, you (the
        customer) are the Controller and SieveGuard is the Processor —{' '}
        <Link to="/dpa">see the DPA</Link> for that relationship.
      </p>

      <h2>2. What personal data we collect</h2>
      <ul>
        <li>
          <strong>Account data</strong>: name, email address, hashed password, role within
          your workspace.
        </li>
        <li>
          <strong>Billing data</strong>: billing email, VAT ID, billing country, the last
          four digits of your payment card (handled by Stripe — we never store card numbers).
        </li>
        <li>
          <strong>Usage data</strong>: API requests, IP address, browser user-agent, response
          status codes — stored for security, debugging, and quota enforcement.
        </li>
        <li>
          <strong>Submitted email data</strong>: email addresses you submit for screening
          (raw + canonical), plus any cluster/score we compute. Processed under the DPA.
        </li>
        <li>
          <strong>Cookies / local storage</strong>: a session cookie for authentication and a
          local-storage value for your theme preference. We do not run third-party trackers.
        </li>
      </ul>

      <h2>3. Lawful basis for processing</h2>
      <ul>
        <li>
          <strong>Contract</strong> (GDPR Art. 6(1)(b)): to provide you the SieveGuard service
          you subscribed to.
        </li>
        <li>
          <strong>Legitimate interests</strong> (GDPR Art. 6(1)(f)): to secure the service,
          detect abuse, and improve the product. We do not use this basis for advertising.
        </li>
        <li>
          <strong>Legal obligation</strong> (GDPR Art. 6(1)(c)): VAT records, tax filings,
          accounting compliance.
        </li>
      </ul>

      <h2>4. Who we share data with</h2>
      <p>
        We use a small number of vetted sub-processors to deliver the service.
        {subprocessorsPage ? (
          <>
            {' '}The full, up-to-date list is at{' '}
            <Link to="/subprocessors">/subprocessors</Link>.
          </>
        ) : (
          <>
            {' '}The current list is available on request to{' '}
            <code>{c.privacyEmail}</code>.
          </>
        )}{' '}
        Each is bound by a written data processing agreement and offers at least the same
        level of protection as this policy.
      </p>

      <h2>5. International transfers</h2>
      <p>
        All customer data is stored in <strong>{c.dataResidencyRegion}</strong>. Where a
        sub-processor (e.g. Stripe, Cloudflare) operates partly outside the EEA, transfers
        are protected by the EU Standard Contractual Clauses (Commission Implementing
        Decision 2021/914), incorporated by reference in each provider&apos;s own DPA.
      </p>

      <h2>6. How long we keep data</h2>
      <ul>
        <li>
          <strong>Account &amp; billing data</strong>: kept while your subscription is active,
          plus 7 years for VAT/tax compliance after termination.
        </li>
        <li>
          <strong>Submitted email data</strong>: kept while your subscription is active. On
          termination, deleted within{' '}
          <strong>{c.retentionDaysAfterTermination} days</strong>. You can request
          immediate purge from the portal at any time.
        </li>
        <li>
          <strong>Verification cache</strong>: deliverability results are cached up to 7
          days, keyed by canonical address (no tenant linkage).
        </li>
        <li>
          <strong>Audit + API request logs</strong>: kept for 90 days.
        </li>
      </ul>

      <h2>7. Your rights under GDPR</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Rectify inaccurate data</li>
        <li>Erase your data (&quot;right to be forgotten&quot;)</li>
        <li>Restrict processing</li>
        <li>Portability (export your data in a machine-readable format)</li>
        <li>Object to processing</li>
        <li>Withdraw consent at any time where consent is the basis</li>
        <li>
          Lodge a complaint with a supervisory authority
          {!c.hideBusinessIdentity && <> (the Maltese IDPC)</>}
        </li>
      </ul>
      <p>
        To exercise any of these, contact <code>{c.privacyEmail}</code>. We respond within
        30 days (extendable by a further 60 in complex cases per GDPR Art. 12(3)).
      </p>

      <h2>8. Security</h2>
      <p>
        TLS 1.3 in transit, AES-256 at rest. Tenant data is isolated at the database layer
        via row-level security. Access to production systems is limited to authorised
        personnel under confidentiality obligations. We notify affected customers within 72
        hours of becoming aware of a personal data breach.
      </p>

      <h2>9. Cookies</h2>
      <p>
        We use strictly-necessary cookies to run the site and app (authentication session,
        theme preference, your cookie choices, affiliate attribution). On our public pages,
        and <strong>only</strong> with your consent, we use Google Analytics to understand
        aggregate, anonymous traffic — it is never loaded until you accept, and you can
        withdraw consent at any time via "Cookie settings" in the footer. We run no
        advertising pixels or marketing trackers. See our{' '}
        <Link to="/cookies">Cookie Policy</Link> for the full list and controls.
      </p>

      <h2>10. Children</h2>
      <p>
        The SieveGuard service is not directed at individuals under 16. We do not knowingly
        collect personal data from children.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes are announced via
        email to the address on file and on the homepage. Continued use of the service
        after the effective date constitutes acceptance.
      </p>

      <h2>12. Contact</h2>
      <p>
        General: <code>{c.supportEmail}</code> · Privacy / GDPR: <code>{c.privacyEmail}</code>
        {!c.hideBusinessIdentity && (
          <>
            {' · '}Postal: {c.legalEntityName}, {c.registeredAddressLine}
          </>
        )}.
      </p>
      {!c.hideBusinessIdentity && (
        <p>
          Supervisory authority: Office of the Information and Data Protection Commissioner
          (IDPC), Floor 2, Airways House, High Street, Sliema SLM 1549, Malta —{' '}
          <a href="https://idpc.org.mt" target="_blank" rel="noreferrer">
            idpc.org.mt
          </a>
          .
        </p>
      )}
    </LegalLayout></>
  );
}

// Default export required by React Router framework-mode route modules.
export default PrivacyPage;
